import type { EngineStatus } from '../client/useSqlite';

interface StatusBarProps {
  engine: EngineStatus;
  activeDb: string | null;
  activeDbBytes: number | null;
  databaseCount: number;
  lastTookMs: number | null;
  rowCount: number | null;
  message: string | null;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function Divider() {
  return <span className="mx-2 h-3 w-px bg-[#4a4a4a]" />;
}

export function StatusBar({
  engine,
  activeDb,
  activeDbBytes,
  databaseCount,
  lastTookMs,
  rowCount,
  message,
}: StatusBarProps) {
  const engineLabel =
    engine.phase === 'ready'
      ? `SQLite ${engine.info.sqliteVersion}`
      : engine.phase === 'loading'
        ? 'Loading engine…'
        : `Engine error: ${engine.message}`;
  const engineColor =
    engine.phase === 'ready'
      ? 'text-[#499c54]'
      : engine.phase === 'error'
        ? 'text-[#ff8888]'
        : 'text-[#bbb529]';

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[#323232] bg-[#3c3f41] px-3 py-1 font-mono text-[11px] text-[#808080]">
      <div className="flex items-center truncate">
        <span className={engineColor}>● {engineLabel}</span>
        {activeDb && (
          <>
            <Divider />
            <span className="text-[#bbbbbb]">{activeDb}</span>
          </>
        )}
        {message && (
          <>
            <Divider />
            <span className="truncate text-[#bbbbbb]">{message}</span>
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center">
        {rowCount != null && (
          <span className="text-[#bbbbbb]">{rowCount.toLocaleString()} rows</span>
        )}
        {lastTookMs != null && (
          <>
            {rowCount != null && <Divider />}
            <span className="tabular-nums text-[#bbbbbb]">{lastTookMs.toFixed(1)} ms</span>
          </>
        )}
        {activeDbBytes != null && activeDbBytes > 0 && (
          <>
            <Divider />
            <span title="Size of the active database file">{formatBytes(activeDbBytes)}</span>
          </>
        )}
        {databaseCount > 0 && (
          <>
            <Divider />
            <span title="Number of databases in browser storage">
              {databaseCount} db{databaseCount === 1 ? '' : 's'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
